import { ApiError, NetworkError } from '@/api/errors';

/** Friendly copy for the error codes a person can act on; anything else shows the server's detail. */
const MESSAGES: Record<string, string> = {
  INVALID_CREDENTIALS: 'Email o contraseña incorrectos.',
  LOGIN_RATE_LIMIT: 'Demasiados intentos. Esperá unos minutos y probá de nuevo.',
  LOGIN_BUSY: 'Hay muchos ingresos al mismo tiempo. Probá de nuevo en unos segundos.',
  FORBIDDEN: 'No tenés permiso para hacer esto. Pedíselo a quien administra el sistema.',
  STALE_VERSION: 'Alguien más cambió esto mientras lo mirabas. Recargá la página para ver cómo quedó.',
  CONCURRENT_EDIT: 'Alguien más cambió esto mientras lo mirabas. Recargá la página para ver cómo quedó.',
  IDEMPOTENCY_CONFLICT: 'Esto ya se había guardado con otros datos. Revisalo y confirmá de nuevo.',
  REVISION_CHANGED: 'Alguien cambió el presupuesto mientras lo mirabas. Ya te mostramos cómo quedó.',
  QUOTE_EXPIRED: 'La fecha «válido hasta» ya pasó. Elegí una fecha de hoy en adelante.',
  MATERIAL_OUTCOME_RETIRED: 'Lo que se usó, sobró o se tiró ahora se anota en la pestaña Materiales, eligiendo la placa.',
  PAYMENT_HAS_FISCAL_APPLICATIONS: 'Este cobro ya tiene una factura hecha. Primero hay que arreglar esa factura y después deshacer el cobro.',
  VERSION_REQUIRED: 'Recargá la página y probá guardar de nuevo.',
  STALE_FX: 'El precio del dólar está viejo. Actualizalo antes de terminar el presupuesto.',
  FX_UNAVAILABLE: 'No pudimos traer el precio del dólar de Ámbito. Probá en un rato o cargalo a mano.',
  SITE_DISTANCE_PENDING: 'Todavía no sabemos a cuántos km está la obra, y sin eso no se calcula el viaje. Tocá «Calcular con el mapa» o cargala a mano.',
  ISSUED_QUOTE: 'Este presupuesto ya tiene PDF (se le pudo haber mandado al cliente): no se elimina, se anula.',
  QUOTE_LINKED: 'Está unido a una consulta: anulalo en vez de eliminarlo.',
  INVALID_DISCOUNT: 'Revisá el descuento: el porcentaje va de 0 a 100 y el monto, sin centavos.',
  DISCOUNT_TOO_HIGH: 'El descuento no puede ser igual o mayor que el total del presupuesto.',
  UNAVAILABLE_ITEM: 'Un material o adicional ya no está en la lista de precios. Cambialo por otro.',
  INVALID_MATERIAL: 'Elegí un material (de los que se venden por m²) para cada pieza.',
  DUPLICATE_EXTRA: 'Ese adicional ya está agregado.',
  WHOLE_UNITS_REQUIRED: 'Esa cantidad tiene que ser un número entero (1, 2, 3…).',
  EMPTY_QUOTE: 'Agregá al menos una pieza.',
  DRAFT_EXISTS: 'Ya hay una versión de este presupuesto que se está armando. Seguí con esa.',
  DISCARDED_QUOTE: 'Este presupuesto se anuló: ya no se puede usar.',
  ACCEPTED_QUOTE: 'El cliente ya aceptó este presupuesto. Para dejarlo sin efecto hay que cancelar el trabajo.',
  CONTRACT_CURRENCY_LOCKED: 'La moneda no se puede cambiar: se mantiene la que aceptó el cliente.',
  WORK_FINISHED: 'El trabajo está cerrado o cancelado.',
  INVALID_PAGE: 'Esa página no existe.',
  // Trabajos
  UNKNOWN_ACTION: 'Eso no se puede hacer acá.',
  WORK_CLOSED: 'El trabajo ya está cerrado.',
  WORK_DELIVERED: 'El trabajo ya se entregó; no se puede cancelar.',
  ALREADY_PAUSED: 'El trabajo ya está pausado.',
  NOT_PAUSED: 'El trabajo no está pausado.',
  WORK_PAUSED: 'El trabajo está pausado. Reanudalo para seguir.',
  TARGET_REQUIRED: 'Elegí a qué etapa avanzar.',
  INVALID_STAGE: 'Solo se puede pasar a una etapa que venga después de la actual.',
  MEASUREMENTS_REQUIRED: 'Primero hay que ir a medir a la obra y anotarlo.',
  DEPOSIT_REQUIRED: 'Falta cobrar la seña. Si se sigue sin ella, usá «Seguir sin la seña» en «Más acciones» y explicá por qué.',
  NOT_DELIVERED: 'Solo se puede cerrar un trabajo entregado o cancelado.',
  PAYMENTS_PENDING: 'Hay pagos sin confirmar. Confirmalos (o rechazalos) antes de cerrar.',
  REVISION_PENDING: 'Hay un cambio del presupuesto que todavía no se resolvió.',
  UNSETTLED_WORK: 'Para cerrar, el cliente no tiene que deber nada ni tener plata a favor.',
  COSTS_INCOMPLETE: 'Antes de cerrar, tocá «Ya cargué todos los gastos».',
  NOT_CLOSED: 'El trabajo no está cerrado.',
  INVALID_SETTLEMENT: 'Lo que se cobra por cancelar no puede ser más que el precio acordado.',
  NOT_READY: 'Para entregar, el trabajo tiene que estar listo o en colocación, y no en pausa.',
  INVALID_PIECE: 'Hay una pieza repetida o que no es de este trabajo.',
  DELIVERY_EXCEEDS_PIECES: 'La cantidad supera las piezas que faltan entregar.',
  INVALID_OUTCOME: 'Revisá los datos del sobrante o de lo que se tiró.',
  INVALID_RANGE: 'El rango de fechas no es válido: elegí uno más corto.',
  // Cobros y caja
  INVALID_AMOUNT: 'El importe tiene que ser mayor que cero.',
  PAYMENT_PRECISION: 'Poné como mucho 2 números después de la coma.',
  PAYMENT_DESTINATION: 'Elegí a qué cuenta nuestra entró la plata.',
  FUTURE_PAYMENT: 'La fecha del pago no puede ser futura.',
  FX_OVERRIDE_REASON_REQUIRED: 'Contá por qué usás un precio del dólar distinto al de hoy, o actualizalo antes de confirmar.',
  FX_REQUIRED: 'Pagó en otra moneda: hace falta el precio del dólar para hacer la cuenta.',
  RATE_REQUIRED: 'Falta el precio del dólar.',
  DUPLICATE_PAYMENT: 'Ese pago ya estaba anotado.',
  PAYMENT_STATE: 'Alguien ya cambió este pago. Recargá la página.',
  PAYMENT_TOO_SMALL: 'El monto es tan chico que, pasado a la otra moneda, da cero.',
  PAYMENT_REFUNDED: 'A este pago ya se le devolvió plata: no se puede deshacer.',
  INVALID_PAYMENT: 'Ese pago no está confirmado o es de otro trabajo.',
  REFUND_LIMIT: 'No se puede devolver más de lo que queda de ese pago.',
  REFUND_CREDIT: 'No se puede devolver más de lo que el cliente pagó de más.',
  ACCOUNT_CURRENCY: 'La cuenta tiene que ser de la misma moneda (pesos o dólares).',
  INVALID_ACCOUNT: 'Esa cuenta no sirve: elegí a nombre de quién está y la moneda.',
  OBLIGATION_WORK: 'Esa deuda no es de este trabajo.',
  // Proveedores y costos
  SUPPLIER_OVERPAYMENT: 'El pago supera lo que se le debe al proveedor.',
  OBLIGATION_CANCELLED: 'Esa deuda ya se dio de baja.',
  OBLIGATION_PAID: 'A esa deuda ya se le pagó algo: no se puede dar de baja.',
  DUPLICATE_OBLIGATION: 'Esa factura del proveedor ya está cargada.',
  INVALID_REVERSAL: 'Esto no se puede deshacer desde acá.',
  ALREADY_REVERSED: 'Ya estaba deshecho.',
  INVALID_COST: 'Revisá el tipo de gasto y el monto.',
  COST_OBLIGATION_MISMATCH: 'El gasto tiene que ser del mismo monto y moneda que la deuda.',
  COST_OF_OBLIGATION: 'Este gasto viene de una compra a un proveedor: dá de baja esa compra para corregirlo.',
  COST_OF_STOCK_ALLOCATION: 'Este gasto viene de una placa del stock: se corrige desde la ficha de esa placa.',
  // Usuarios y roles
  SELF_DISABLE: 'No podés quitarte el acceso a vos mismo.',
  ROLE_IN_USE: 'Quitale el rol a sus usuarios (y sus reglas de IA) antes de borrarlo.',
  SYSTEM_ROLE: 'El rol de sistema no se puede borrar.',
  SYSTEM_ROLE_GRANTS: 'El rol Administración siempre puede todo. Para uno con menos permisos, duplicalo y sacale lo que no va.',
  ROLE_NAME_TAKEN: 'Ya hay un rol con ese nombre.',
  ALERT_REVIEWED: 'Esa alerta ya fue revisada.',
  // Agenda, mapas y rutas
  WORK_NOT_ACTIVE: 'El trabajo está cerrado o cancelado: no se le programan visitas.',
  VISIT_CLOSED: 'La visita ya está hecha o cancelada.',
  INVALID_WINDOW: 'La hora de inicio tiene que ser anterior a la de fin.',
  NO_STOPS: 'No hay visitas programadas para ese día.',
  TOO_MANY_STOPS: 'Google ordena hasta 25 paradas por día: pasá algunas visitas a otro día.',
  STOPS_CHANGED: 'Cambiaron las visitas mientras se calculaba la ruta: volvé a ordenarla.',
  MAPS_UNAVAILABLE: 'El mapa no está disponible: cargá la dirección del taller en Configuración, o la distancia a mano.',
  NO_ROUTE: 'El mapa no encontró un recorrido: revisá la dirección o cargá la distancia a mano.',
  INVALID_GEO: 'La ubicación elegida no es válida.',
  // Configuración, marketing y fiscal
  ACTIVATION_EVIDENCE: 'Contá cómo se activó la campaña (link, captura o referencia).',
  CAMPAIGN_NOT_PLANNED: 'Solo se edita una campaña mientras está planificada.',
  PLAN_HAS_ISSUES: 'El plan tiene cosas para revisar. Miralas antes de usarlo.',
  META_NOT_CONFIGURED: 'Meta todavía no está conectado.',
  META_ADS_NOT_CONFIGURED: 'Falta la cuenta publicitaria o la página de Meta en Configuración.',
  NOT_LINKED: 'La campaña todavía no está vinculada con Meta.',
  NO_TEST: 'Esta conexión no se puede probar desde acá.',
  SECRET_UNREADABLE: 'Una de las claves guardadas no se puede leer. Volvé a cargarla.',
  MASTER_KEY_MISSING: 'El servidor no está preparado para guardar claves. Avisale a quien instaló el sistema.',
};

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === 'VALIDATION') return 'Hay datos para revisar: están marcados en rojo.';
    if (error.status === 403) return MESSAGES.FORBIDDEN!;
    return MESSAGES[error.code] ?? (error.detail || 'No se pudo hacer. Probá de nuevo en un momento.');
  }
  if (error instanceof NetworkError) return error.message;
  if (error instanceof Error && error.name === 'AbortError') return '';
  return 'Algo salió mal. Probá de nuevo.';
}

export function requestIdOf(error: unknown): string | null {
  return error instanceof ApiError ? error.requestId : null;
}
